import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import { LANGUAGE_LABELS, languageCode, useLanguageSelection } from '../lib/language';

export type LanguageSwitcherProps = {
    variant?: 'outlined' | 'text';
};

const VARIANT_SX = {
    outlined: {
        minWidth: 0,
        width: 36,
        height: 36,
        p: 0,
        letterSpacing: '0.25px',
    },
    text: {
        minWidth: 0,
        px: 2,
        color: 'text.secondary',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '0.25px',
    },
} as const;

export function LanguageSwitcher({ variant = 'outlined' }: LanguageSwitcherProps): ReactElement {
    const { t } = useTranslation();
    const { activeLanguage, select } = useLanguageSelection();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);

    const handleSelect = async (language: SupportedLanguage): Promise<void> => {
        setOpen(false);
        await select(language);
    };

    return (
        <>
            <Button
                ref={anchorRef}
                variant={variant}
                color="primary"
                onClick={() => setOpen(true)}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={t('HEADER.LANGUAGE_ARIA')}
                sx={VARIANT_SX[variant]}>
                {languageCode(activeLanguage)}
            </Button>
            <Menu
                anchorEl={anchorRef.current}
                open={open}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { elevation: 8, sx: { mt: 0.5 } } }}>
                {SUPPORTED_LANGUAGES.map((language) => (
                    <MenuItem
                        key={language}
                        selected={language === activeLanguage}
                        onClick={() => void handleSelect(language)}>
                        {LANGUAGE_LABELS[language]}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
